import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ERROR_CODES } from "@/lib/error-messages";
import { initiateBankId, verifyBankIdSession } from "../thunks/bankid.thunks";

export type LoadingState = "idle" | "pending" | "fulfilled" | "rejected";

export interface SPARv2SvenskAdress {
  ns2DatumFrom: string;
  ns2DatumTill: string;
  ns3Utdelningsadress2: string;
  ns3PostNr: string;
  ns3Postort: string;
}

export interface SPARv2Folkbokforingsadress {
  ns3SvenskAdress: SPARv2SvenskAdress;
}

export interface SPARv2Namn {
  ns2DatumFrom: string;
  ns2DatumTill: string;
  ns7Fornamn: string;
  ns7Tilltalsnamn: string;
  ns7Efternamn: string;
}

export interface SPARv2Persondetaljer {
  ns2DatumFrom: string;
  ns2DatumTill: string;
  ns5Sekretessmarkering: "JA" | "NEJ";
  ns5SkyddadFolkbokforing: "JA" | "NEJ";
  ns10Fodelsedatum: string;
  ns10Kon: "MAN" | "KVINNA";
}

export interface SPARv2Data {
  ns4PersonId: {
    ns4IdNummer: string;
    ns4Typ: "PERSONNUMMER";
  };
  ns5Sekretessmarkering: "JA" | "NEJ";
  ns5SkyddadFolkbokforing: "JA" | "NEJ";
  ns4SenasteAndringSPAR: string;
  ns8Namn: SPARv2Namn;
  ns10Persondetaljer: SPARv2Persondetaljer;
  ns15Folkbokforingsadress:
    | SPARv2Folkbokforingsadress
    | SPARv2Folkbokforingsadress[];
}

// BankID types
export interface BankIdUserAttributes {
  name: string;
  givenName: string;
  surname: string;
  personalNumber: string;
  ipAddress?: string;
  notBefore?: string;
  notAfter?: string;
  signature?: string;
  ocspResponse?: string;
  SPARv2?: SPARv2Data;
}

export interface BankIdSessionResponse {
  sessionId: string;
  username: string;
  userAttributes: BankIdUserAttributes;
}

export interface BankIdVerificationSuccess {
  name: string;
  personalNumber: string;
}

export interface InitiateBankIdRequest {
  orderId: string;
  callbackUrl: string;
  thisDevice?: boolean;
  mobileBankId?: boolean;
  askForSSN?: boolean;
  deviceChoice?: boolean;
  qr?: boolean;
  personalNumber?: string;
}

export interface InitiateBankIdResponse {
  sessionId: string;
  redirectUrl: string;
}

interface BankIdState {
  initiateBankIdLoading: LoadingState;
  verifyBankIdSessionLoading: LoadingState;
  error: string | null;
  verificationSuccess: BankIdVerificationSuccess | null;
  verificationDetails: BankIdSessionResponse | null;
  email: string;
  emailError: string | null;
  phone: string;
  phoneError: string | null;
}

const initialState: BankIdState = {
  initiateBankIdLoading: "idle",
  verifyBankIdSessionLoading: "idle",
  error: null,
  verificationSuccess: null,
  verificationDetails: null,
  email: "",
  emailError: null,
  phone: "",
  phoneError: null,
};

const bankIdSlice = createSlice({
  name: "bankId",
  initialState,
  reducers: {
    resetBankIdState: () => initialState,

    setBankIdEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
      state.emailError = null;
    },

    setBankIdEmailError: (state, action: PayloadAction<string | null>) => {
      state.emailError = action.payload;
    },

    setBankIdPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
      state.phoneError = null;
    },

    setBankIdPhoneError: (state, action: PayloadAction<string | null>) => {
      state.phoneError = action.payload;
    },

    restoreBankIdFor3dsResume: (
      state,
      action: PayloadAction<{
        verificationDetails: BankIdSessionResponse;
        email: string;
        phone: string;
      }>
    ) => {
      const { verificationDetails: vd } = action.payload;
      state.verificationDetails = vd;
      state.email = action.payload.email;
      state.phone = action.payload.phone;
      state.verifyBankIdSessionLoading = "fulfilled";
      state.initiateBankIdLoading = "fulfilled";
      state.error = null;
      if (vd.userAttributes) {
        const maskedPnr = vd.userAttributes.personalNumber.replace(
          /(\d{8})(\d{4})/,
          "$1-****"
        );

        const formatName = (name: string) =>
          name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

        const formattedGivenName = formatName(vd.userAttributes.givenName);
        const formattedSurname = formatName(vd.userAttributes.surname);

        state.verificationSuccess = {
          name: `${formattedGivenName} ${formattedSurname}`,
          personalNumber: maskedPnr,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // BankID Initiation
      .addCase(initiateBankId.pending, (state) => {
        state.initiateBankIdLoading = "pending";
        state.error = null;
      })
      .addCase(initiateBankId.fulfilled, (state) => {
        state.initiateBankIdLoading = "fulfilled";
      })
      .addCase(initiateBankId.rejected, (state, action) => {
        state.initiateBankIdLoading = "rejected";
        state.error =
          (action.payload as string) ?? ERROR_CODES.FAILED_TO_INITIATE_BANKID;
      })

      // BankID Session Verification
      .addCase(verifyBankIdSession.pending, (state) => {
        state.verifyBankIdSessionLoading = "pending";
        state.error = null;
      })
      .addCase(
        verifyBankIdSession.fulfilled,
        (state, action: PayloadAction<BankIdSessionResponse>) => {
          state.verifyBankIdSessionLoading = "fulfilled";
          state.verificationDetails = action.payload;

          if (action.payload.userAttributes) {
            const maskedPnr =
              action.payload.userAttributes.personalNumber.replace(
                /(\d{8})(\d{4})/,
                "$1-****"
              );

            const formatName = (name: string) =>
              name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

            const formattedGivenName = formatName(
              action.payload.userAttributes.givenName
            );
            const formattedSurname = formatName(
              action.payload.userAttributes.surname
            );

            state.verificationSuccess = {
              name: `${formattedGivenName} ${formattedSurname}`,
              personalNumber: maskedPnr,
            };
          }
        }
      )
      .addCase(verifyBankIdSession.rejected, (state, action) => {
        state.verifyBankIdSessionLoading = "rejected";
        state.error =
          (action.payload as string) ?? ERROR_CODES.FAILED_TO_VERIFY_BANKID;
      });
  },
});

export const {
  resetBankIdState,
  setBankIdEmail,
  setBankIdEmailError,
  setBankIdPhone,
  setBankIdPhoneError,
  restoreBankIdFor3dsResume,
} = bankIdSlice.actions;

export default bankIdSlice.reducer;
